import {
  DesignToken,
  ThemeName,
} from '@design/design-tokens/themes/theme-names';
import { getThemeColor } from '@design/design-tokens/themes/theme-utils';
import { useWindowResize } from '@helpers/hooks/use-window-resize/useWindowResize';
import clsx from 'clsx';
import { getInstanceByDom, init } from 'echarts';
import { useEffect, useRef, useState } from 'react';

import styles from './PieChart.module.scss';
import { createPieSeries, defaultFormatLegendValue, getColors } from './utils';

export type PieChartData = {
  value: number;
  name: string;
  color?: DesignToken | string;
}[];
export interface PieChartProps {
  data: PieChartData;
  theme?: ThemeName;
  hideLegend?: boolean;
  solid?: boolean;
  disableHover?: boolean;
  disableAnimation?: boolean;
  formatLegendValue?: (value: number) => string;
}

export const PieChart = ({
  data,
  theme = 'dark_default',
  hideLegend = false,
  solid = false,
  disableHover = false,
  disableAnimation = false,
  formatLegendValue = defaultFormatLegendValue,
}: PieChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const normalColors = getColors(theme);
  const dataKey = JSON.stringify(data);

  const onLegendEnter = (i: number) => {
    setActiveIndex(i);

    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: i,
      });
    }
  };

  const onLegendLeave = (i: number) => {
    setActiveIndex(null);

    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.dispatchAction({ type: 'downplay', seriesIndex: 0, dataIndex: i });
    }
  };

  useEffect(() => {
    if (!ref.current || !width || !height) {
      return;
    }

    const chart = getInstanceByDom(ref.current);
    const chartWidth = chart?.getWidth();
    const chartHeight = chart?.getHeight();
    const sameSize =
      chartWidth != null &&
      chartHeight != null &&
      Math.abs(chartWidth - width) < 1 &&
      Math.abs(chartHeight - height) < 1;

    if (!chart || sameSize) {
      return;
    }

    chart.resize({ width, height });
  }, [width, height]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dataKey compares slice content; depending on data re-inits the chart when the array reference changes.
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const chart = init(ref.current, theme);
    chart.setOption({
      animation: !disableAnimation,
      legend: { show: false },
      series: createPieSeries(data, theme, { solid, disableHover }),
    });

    if (!disableHover) {
      chart.on('mouseover', params => {
        if (params.componentType === 'series') {
          setActiveIndex(params.dataIndex);
        }
      });
      chart.on('mouseout', () => {
        setActiveIndex(null);
      });
    }

    return () => {
      chart.dispose();
    };
  }, [dataKey, theme, solid, disableHover, disableAnimation]);

  return (
    <div className={styles.root} data-pie-chart-root>
      <div className={styles.chartWrapper} data-pie-chart-canvas ref={ref} />
      {!hideLegend && (
        <div className={styles.legendWrapper} data-pie-chart-legend>
          {data.map((item, idx) => (
            <div
              className={clsx(styles.row, {
                [styles.rowActive]: activeIndex === idx,
              })}
              onMouseEnter={() => onLegendEnter(idx)}
              onMouseLeave={() => onLegendLeave(idx)}
              key={`row-${idx}`}
            >
              <div className={styles.legendNameWrapper}>
                <div
                  className={styles.styledDot}
                  style={{
                    backgroundColor: item.color
                      ? getThemeColor(item.color, theme)
                      : normalColors[idx % normalColors.length],
                  }}
                />
                <div className={styles.bodySmallText}>{item.name}</div>
              </div>
              <div className={styles.bodySmallText}>
                {formatLegendValue(item.value)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
